import { ApolloClient } from 'apollo-client'
import { createHttpLink } from 'apollo-link-http'
import { setContext } from 'apollo-link-context'
import { onError } from 'apollo-link-error'
import { fromPromise } from 'apollo-link'
import { InMemoryCache } from 'apollo-cache-inmemory'
import { getToken, getRefreshToken, setToken, logout } from './auth'

const GRAPHQL_URI = process.env.VUE_APP_GRAPHQL_URI || 'http://localhost:8000/graphql/'

// HTTP connection to the API
const httpLink = createHttpLink({ uri: GRAPHQL_URI })

// Attach the JWT (if any) to every request
const authLink = setContext((_, { headers }) => {
  const token = getToken()
  return {
    headers: {
      ...headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  }
})

// --- Transparent access-token refresh -----------------------------------
// The backend rejects an expired access token with a GraphQL error whose
// message tells the user to log in. When that happens and we still hold a
// refresh token, we silently exchange it for a fresh access token and retry
// the failed operation once. A single in-flight refresh is shared across
// concurrent failures.
let refreshing = null

function refreshAccessToken() {
  const refresh = getRefreshToken()
  if (!refresh) return Promise.reject(new Error('no-refresh-token'))
  if (!refreshing) {
    refreshing = fetch(GRAPHQL_URI, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: `mutation Refresh($r: String!) { refreshToken(refreshToken: $r) { token refreshToken } }`,
        variables: { r: refresh },
      }),
    })
      .then((res) => res.json())
      .then((json) => {
        const payload = json?.data?.refreshToken
        if (!payload?.token) throw new Error('refresh-failed')
        setToken(payload.token)
        return payload.token
      })
      .finally(() => { refreshing = null })
  }
  return refreshing
}

// Messages the backend uses to signal an auth problem — we try a token refresh
// on any of these. Kept broad on purpose: the backend phrases auth errors in
// French ("Connecte-toi pour…", "Réservé aux modérateurs.", "Connecte-toi pour
// effectuer cette action.") and we don't want a new wording to silently break
// the refresh flow.
const AUTH_ERROR_HINTS = [
  'connecte-toi',
  'connexion',
  'authentification',
  'token',
  'réservé aux modérateur',
  'reserve aux moderateur',
  'non authentifié',
  'not authenticated',
]

const errorLink = onError(({ graphQLErrors, operation, forward }) => {
  if (!graphQLErrors) return
  const isAuthError = graphQLErrors.some((e) =>
    AUTH_ERROR_HINTS.some((h) => (e.message || '').toLowerCase().includes(h)),
  )
  if (!isAuthError || !getRefreshToken()) return

  // Refresh then replay the operation once. If the refresh itself fails (the
  // refresh token is gone or expired too), log out and bounce to /login,
  // remembering where the user was so they land back there after signing in.
  return fromPromise(
    refreshAccessToken().catch(() => {
      logout()
      const path = window.location.pathname + window.location.search
      if (!path.startsWith('/login')) {
        window.location.assign(`/login?redirect=${encodeURIComponent(path)}`)
      }
      return null
    }),
  )
    .filter((token) => !!token)
    .flatMap((token) => {
      const oldHeaders = operation.getContext().headers || {}
      operation.setContext({
        headers: { ...oldHeaders, Authorization: `Bearer ${token}` },
      })
      return forward(operation)
    })
})

const cache = new InMemoryCache()

const apolloClient = new ApolloClient({
  link: errorLink.concat(authLink).concat(httpLink),
  cache,
})

export default apolloClient
